import { useLayoutEffect, useEffect, useState } from "react";
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign, FaTimesCircle, FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { employeesAPI, transportsAPI, usersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row } from "react-bootstrap";
import Swal from 'sweetalert2';
import { formatNumber } from '../../utils/helpers';

const EmployeeTransport = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [counts, setCounts] = useState({
    all: 0,
    car: 0,
    furniture: 0,
    completed: 0
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  // Fetch transports based on tab and search
  const fetchTransports = async (page = 1, search = searchTerm, tab = activeTab) => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      switch (tab) {
        case "car":
          response = await employeesAPI.carTransports(employeeId, params);
          break;
        case "furniture":
          response = await employeesAPI.furnitureTransports(employeeId, params);
          break;
        case "completed":
          response = await employeesAPI.completedTransports(employeeId, params);
          break;
        default:
          response = await employeesAPI.transports(employeeId, params);
      }

      if (response.success) {
        // معالجة البيانات حسب نوع الـ tab
        let fetchedData = [];
        let metaData = {};
        
        // بيانات transportsAPI
        fetchedData = response.data.data || response.data;
        metaData = response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        };
        
        if (response.data.counts) {
          setCounts(prev => ({
            ...prev,
            all: response.data.counts.all || 0,
            car: response.data.counts.car || 0,
            furniture: response.data.counts.furniture || 0,
            completed: response.data.counts.completed || 0,
          }));
        }

        setData(fetchedData);
        setPagination(metaData);

        // Save current state in URL
        navigate(location.pathname + location.search, { 
          state: { activeTab: tab, currentPage: page },
          replace: true,
        });
      } else {
        setError(response.message || t('failed_to_fetch_transports'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transports'));
      console.error('Error fetching transports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchTransports();
      fetchAllCounts();
      fetchEmployee();
    } else {
      console.warn("⚠️ employeeId is missing from URL");
      setError(t("employee_id_missing") || "Employee ID is missing");
      setLoading(false);
    }
  }, [employeeId]);

  // Fetch employee details by id
    const fetchEmployee = async () => {
      try {
        const employeeResponse = await usersAPI.show(employeeId);
        if (employeeResponse.success) {
          setEmployee(employeeResponse.data.data || employeeResponse.data);
        }
      } catch (err) {
        console.error('Error fetching employee details:', err);
      }
    };

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      // جلب عدد transports
      const transportResponse = await employeesAPI.transports(employeeId, ({ per_page: 1 }));
      if (transportResponse.success && transportResponse.data.counts) {
        setCounts(prev => ({
          ...prev,
          all: transportResponse.data.counts.all || 0,
          car: transportResponse.data.counts.car || 0,
          furniture: transportResponse.data.counts.furniture || 0,
          completed: transportResponse.data.counts.completed || 0,
        }));
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleMarkPaid = async (id) => {
    Swal.fire({
      title: t("confirm_action"),
      text: t("confirm_mark_paid"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("yes_mark_paid"),
      cancelButtonText: t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setUpdating(true);
          const response = await transportsAPI.markPaid(id);
          
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: t("success"),
              text: t("booking.marked_paid_successfully"),
              timer: 3000,
              showConfirmButton: false,
            });
            fetchTransports();
            fetchAllCounts();
          } else {
            throw new Error(response.message);
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: err.message || t("failed_to_mark_paid"),
            timer: 3000,
          });
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchTransports(1, searchTerm, activeTab);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (activeTab && employeeId) {
      fetchTransports(1, searchTerm, activeTab);
    }
  }, [activeTab, employeeId]);

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchAllCounts();
    
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransports(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await transportsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking.transport_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchTransports(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_transport'));
      }
    } catch (err) {
      setError(t('failed_to_delete_transport'));
      console.error('Error deleting transport:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    const path = `/admin/transports/details/${id}`;
    
    navigate(path, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (item) => {
    if (!item) return 0;
    
    const statusOrder = ['pending', 'approved', 'assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(item.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: 'danger', text: t('cancelled') },
      rejected: { variant: 'danger', text: t('rejected') },
      approved: { variant: 'success', text: t('approved') },
      assigned: { variant: 'info', text: t('assigned') },
      received: { variant: 'secondary', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'warning', text: t('pending') }
    };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
    fetchTransports(1, '');
  };

  // دالة لعرض العنصر حسب نوع الـ tab
  const renderItem = (item, index) => {
    const itemId = item.booking_id;
    const status = item.status;
    const user = item.user?.user_name;
    const bookingFrom = item.booking_from;
    const bookingTo = item.booking_to;
    const distance = item.distance;
    const totalWeight = item.total_weight;
    const transportType = item.transport_type;
    const itemType = item.item_type?.[0];
    const numItems = item.num_items?.[0];
    const dimensions = item.dimensions?.[0];
    const createdAt = item.created_at;

    return (
      <div 
        className={`order-card ${item.id === highlightedBookingId ? "booking-highlight" : ""}`} 
        key={item.id}
        id={`booking-${item.id}`}
      >
        <div className="order-header d-flex justify-content-between align-items-center flex-wrap">

          <span className="order-id">
            <strong>
              {t("Auftrag Nr.")}:
              {searchTerm ? (
                <span 
                  dangerouslySetInnerHTML={{
                    __html: itemId?.replace(
                      new RegExp(searchTerm, 'gi'), 
                      match => `<mark class="bg-warning">${match}</mark>`
                    )
                  }} 
                />
              ) : (
                itemId
              )}
            </strong>
          </span>

          <span className="driver">
            <strong>{t("user")}:</strong> 
            {user || t('not_assigned')}
          </span>

          <span className="created">{item.created_at}</span> 

          <span className="price d-flex justify-content-end flex-wrap">
            {getStatusBadge(status)}

            <Badge bg={item.payment_status ? 'success' : 'warning'} className="ms-2">
              {t(item.payment) || t('unpaid')}
            </Badge>

            <Badge bg="info" className="ms-2">
              {t('order')}
            </Badge>
            {item.price&& (
              <Badge bg="light" text="dark" className="ms-2">
                {item.price} €
              </Badge>
            )}

            {item.invoice_status === 'canceled' && (
              <Badge bg="danger" className="ms-2">
                {t('canceled')}
              </Badge>
            )}

            {(transportType == "car" ? (
                <Badge bg={'success'} className="ms-2">
                  {t('car')}
                </Badge>
              ) : (
                <Badge bg={'success'} className="ms-2">
                  {t('furniture')}
                </Badge>
              )
            )}
          </span>
        </div>

        <div className="order-body">
          <div className="left d-flex justify-content-between flex-wrap">
            <div className="d-flex flex-column">
              <strong>{t("booking.transport_type")}:</strong> 
              {t(transportType) || t('not_available')}
            </div>
            <div className="d-flex flex-column">
              <strong>{t("booking.item_type")}:</strong> 
              {itemType || t('not_available')}
            </div>
            <div className="d-flex flex-column">
              {numItems && numItems !== '' ? (
                <>
                  <strong>{t("booking.num_items")}:</strong> {numItems} {t('items')}
                </>
              ) : null}
            </div>

            <div className="delivery-path-vertical">
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingFrom}</span>
              </div>
              <div className="line-container">
                <div className="line-vertical"></div>
              </div>
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingTo}</span>
              </div>
            </div>
          </div>

          <div className="right d-flex justify-content-between">
              <div className="progress-bar-container mt-2">
                <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                  <strong>{t("progress")}:</strong>
                  <span className="progress-label">{getProgressPercentage(item)}%</span>
                </div>
                
                <div className="progress-bar-outer">
                  <div
                    className="progress-bar-inner"
                    style={{
                      width: `${getProgressPercentage(item)}%`,
                      background: getProgressColor(getProgressPercentage(item)),
                    }}
                  ></div>
                </div>
              </div>
            
            {dimensions && (
              <span className="price">
                <strong>{t("booking.dimensions")}:</strong> {dimensions}
              </span>
            )}
            
          </div>
        </div>

        <div className="order-footer">
          <div>
            <strong>{t("booking.loading_date")}:</strong>{" "}
            {formatDate(item.loading_date || item.transport?.loading_date || item.created_at)}
          </div>
          <div>
            <strong>{t("distance")}:</strong> {distance} | 
            <strong> {t("duration")}:</strong> {item.total_duration || item.transport?.total_duration}
          </div>
          <div>
            <strong>{t("total_weight")}:</strong> {formatNumber(totalWeight)} Kg
          </div>

          <div className="action-buttons">
            {item.payment_status === 0 && (
              <Button 
                size="sm"
                variant="outline-success"
                onClick={() => handleMarkPaid(item.id)}
                disabled={updating || item.payment_status}
              >
                <FaEuroSign /> {t("booking.mark_paid")}
              </Button>
            )}
            
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleViewDetails(item.id)}
              className="mx-2"
            >
              <FaInfoCircle /> {t("details")}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeleteClick(item.id)}
            >
              <FaTrashAlt /> {t("delete")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="driver-page">
        <div className="top-bar">
          {/* <div className="d-flex gap-2"> */}
            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('search_transport')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col md={4}>
                {employee && (
                  <div>
                    <strong>{t("booking_user")}:</strong> <span className="ms-2 text-success cursor-pointer" onClick={ navigate(`/admin/employees/details/${employee.id}`) }>{employee.user_name || employee.name || t('not_available')}</span>
                  </div>
                )}
              </Col>
            </Row>
          {/* </div> */}
          
          <div className="tabs">
            <button
              className={activeTab === "active" ? "active" : ""}
              onClick={() => handleTabChange("active")}
            >
              <span className="count-badge">{counts.all || 0}</span>
              {t("all_transports")}
            </button>
            <button
              className={activeTab === "car" ? "active" : ""}
              onClick={() => handleTabChange("car")}
            >
              <span className="count-badge">{counts.car}</span>
              {t("car_transports")}
            </button>
            <button
              className={activeTab === "furniture" ? "active" : ""}
              onClick={() => handleTabChange("furniture")}
            >
              <span className="count-badge">{counts.furniture}</span>
              {t("furniture_transports")}
            </button>
            <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
            >
              <span className="count-badge">{counts.completed}</span>
              {t("completed_transports")}
            </button>
          </div>
          
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="orders">
          {data.length === 0 ? (
            <div className="text-center py-5">
              <h4>{t('no_transports_found')}</h4>
            </div>
          ) : (
            data.map((item, index) => renderItem(item, index))
          )}
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchTransports}
            disabled={loading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('booking.confirm_delete_transport')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default EmployeeTransport;